import { useEffect } from 'react'
import { useSetInteractiveElements, type Bounds } from './use-interactive'
import { interactiveFramePublisher } from './interactive-frame'
import { boundsRecordsEqual, interactiveRegistry } from './registry'

/** Re-measure at most this often; each tick reads current bounds so the last is always fresh. */
const BOUNDS_UPDATE_INTERVAL_MS = 149

export function refreshInteractiveBounds(): void {
  interactiveRegistry.requestMeasurement()
}

/**
 * Measure and publish a frame immediately, even when the measured bounds are
 * unchanged. {@link refreshInteractiveBounds} is deduped by the loop and
 * publishes nothing without a bounds delta.
 *
 * For surfaces that mount without moving any tracker: a drill-in view sits
 * inside the panel band's already-registered region, so swapping views changes
 * no bounds and the loop stays silent. Main would keep its stale click-through
 * verdict until the first mousemove (the cursor poll deliberately ignores a
 * stationary cursor), and multi-touch gestures aren't forwarded while
 * click-through, so they'd fall through to the window behind. A bounds frame
 * makes main re-hit-test the stationary cursor at mount, before any pointer
 * event arrives.
 */
export function syncInteractiveRegion(): void {
  interactiveFramePublisher.publishBounds(interactiveRegistry.measure())
}

/**
 * Runs the single shared measurement loop for every registered interactive
 * element. Mounted once (by the provider) in the overlay renderer.
 *
 * Why a fixed-interval poll rather than DOM events: the things that move these
 * elements are framer-motion transform animations — the sidepanel slide, view
 * swaps, swipe-to-close — which change `getBoundingClientRect()` every frame
 * but fire no `scroll`/`resize`/ResizeObserver event to listen for. A poll is the
 * only reliable signal. Registry changes (mounts/unmounts) and explicit
 * `refreshInteractiveBounds()` calls flush immediately; the interval only
 * covers silent movement between those events.
 */
export function useInteractiveBoundsSync(): void {
  const setInteractiveElements = useSetInteractiveElements()

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null
    let flushScheduled = false
    let disposed = false
    let previous: Record<string, Bounds> = {}

    const flush = () => {
      const next = interactiveRegistry.measure()
      if (boundsRecordsEqual(previous, next)) return
      previous = next
      setInteractiveElements(next)
      interactiveFramePublisher.publishBounds(next)
    }

    // Coalesce a burst of mounts/unmounts into one measurement. Use a microtask,
    // NOT requestAnimationFrame: the overlay window runs with
    // `backgroundThrottling: false` and only paints on demand, so when content
    // first mounts (or the panel opens while the window isn't compositing) an rAF
    // callback may not fire for a long time — which would leave the panel
    // click-through until the user nudges it. Microtasks always drain, so the
    // first bounds reach the main process the same tick the UI commits.
    const scheduleFlush = () => {
      if (flushScheduled) return
      flushScheduled = true
      queueMicrotask(() => {
        flushScheduled = false
        if (!disposed) flush()
      })
    }

    const onRegistryChange = () => {
      if (interactiveRegistry.size > 0) {
        if (interval === null) interval = setInterval(flush, BOUNDS_UPDATE_INTERVAL_MS)
        scheduleFlush()
        return
      }
      // No elements left: stop the loop and clear stale bounds synchronously —
      // flush() measures the now-empty registry, so the atom, the registry's
      // lastMeasured snapshot, and main's copy all empty in the same tick and
      // the window can't stay interactive over phantom regions.
      if (interval !== null) {
        clearInterval(interval)
        interval = null
      }
      flush()
    }

    const unsubscribe = interactiveRegistry.subscribe(onRegistryChange)
    const unsubscribeMeasurementRequests = interactiveRegistry.subscribeToMeasurementRequests(scheduleFlush)
    // Programmatic BrowserWindow.setBounds() calls resize the overlay viewport
    // when it moves between differently sized displays. Flush immediately so
    // hit-testing does not use the previous display's layout until the poll.
    window.addEventListener('resize', scheduleFlush)
    // Trackers mount before the provider's effect (effects run bottom-up), so
    // sync once now to pick up anything already registered.
    onRegistryChange()

    return () => {
      disposed = true
      unsubscribe()
      unsubscribeMeasurementRequests()
      window.removeEventListener('resize', scheduleFlush)
      if (interval !== null) clearInterval(interval)
    }
  }, [setInteractiveElements])
}
